import React, { useState, useEffect } from 'react';
import { Card, Button, ListGroup, Badge, Spinner, Tab, Tabs } from 'react-bootstrap';
import { MessageSquare, Send, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { showError } from '../../../utils/notifications';
import standardMessageService, { StandardMessage } from '../../../services/standardMessageService';
import { useUserData } from '../../../hooks';

const StandardMessages = () => {
    const navigate = useNavigate();
    const { canEdit } = useUserData();
    const [templates, setTemplates] = useState<StandardMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState<StandardMessage | null>(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const data = await standardMessageService.getByType('message');
            setTemplates(data);
            if (data.length > 0) {
                setSelectedTemplate(data[0]);
            }
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to load templates');
            console.error('Error loading templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleComposeMessage = () => {
        navigate('/cms/standard-messages/composer');
    };

    if (loading) {
        return (
            <div>
                <h4 className="mb-4 d-none d-lg-block">Messages Management</h4>
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading templates...</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 d-none d-lg-block">Messages Management</h4>
                {canEdit && (
                    <Button variant="primary" onClick={handleComposeMessage}>
                        <Send size={18} className="me-2" />
                        Compose Message
                    </Button>
                )}
            </div>

            {templates.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <MessageSquare size={48} className="text-muted mb-3" />
                        <p className="text-muted">No message templates found</p>
                    </Card.Body>
                </Card>
            ) : (
                <div className="row">
                    {/* Templates List */}
                    <div className="col-md-4">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <div className="d-flex align-items-center">
                                    <List size={18} className="me-2" />
                                    <strong>Message Templates</strong>
                                </div>
                            </Card.Header>
                            <ListGroup variant="flush">
                                {templates.map((template) => (
                                    <ListGroup.Item
                                        key={template.id}
                                        active={selectedTemplate?.id === template.id}
                                        onClick={() => setSelectedTemplate(template)}
                                        style={{ cursor: 'pointer' }}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <div>
                                            <div className="fw-semibold">{template.name}</div>
                                        </div>
                                        {template.is_active && (
                                            <Badge bg="success">Active</Badge>
                                        )}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card>
                    </div>

                    {/* Template Preview */}
                    <div className="col-md-8">
                        {selectedTemplate ? (
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white border-bottom">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <strong>{selectedTemplate.name}</strong>
                                        {canEdit && (
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={handleComposeMessage}
                                            >
                                                <Send size={14} className="me-1" />
                                                Use Template
                                            </Button>
                                        )}
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <Tabs defaultActiveKey="spanish" className="mb-3">
                                        <Tab eventKey="spanish" title="🇪🇸 Spanish">
                                            <div
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '20px',
                                                    borderRadius: '8px',
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9rem',
                                                    lineHeight: '1.6',
                                                }}
                                            >
                                                {selectedTemplate.spanish_template}
                                            </div>
                                        </Tab>
                                        <Tab eventKey="english" title="🇬🇧 English">
                                            <div
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '20px',
                                                    borderRadius: '8px',
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9rem',
                                                    lineHeight: '1.6',
                                                }}
                                            >
                                                {selectedTemplate.english_template}
                                            </div>
                                        </Tab>
                                        <Tab eventKey="dutch" title="🇳🇱 Dutch">
                                            <div
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '20px',
                                                    borderRadius: '8px',
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9rem',
                                                    lineHeight: '1.6',
                                                }}
                                            >
                                                {selectedTemplate.dutch_template}
                                            </div>
                                        </Tab>
                                        <Tab eventKey="french" title="🇫🇷 French">
                                            <div
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '20px',
                                                    borderRadius: '8px',
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9rem',
                                                    lineHeight: '1.6',
                                                }}
                                            >
                                                {selectedTemplate.french_template}
                                            </div>
                                        </Tab>
                                    </Tabs>

                                    <div className="mt-3 p-3 bg-light rounded">
                                        <strong className="d-block mb-2">Available Variables:</strong>
                                        <div className="d-flex flex-wrap gap-2">
                                            <Badge bg="secondary">{'{{guest_name}}'}</Badge>
                                            <Badge bg="secondary">{'{{house_name}}'}</Badge>
                                            <Badge bg="secondary">{'{{house_address}}'}</Badge>
                                            <Badge bg="secondary">{'{{check_in_date}}'}</Badge>
                                            <Badge bg="secondary">{'{{check_out_date}}'}</Badge>
                                            <Badge bg="secondary">{'{{deposit_amount}}'}</Badge>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        ) : (
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="text-center py-5">
                                    <p className="text-muted">Select a template to preview</p>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandardMessages;
